'use client';

import type { ChoiceType } from '~/model/ChoiceType';
import type { UrlType } from '~/model/GlobalTypes';
import type { ResultsQuestionsValueType } from '~/model/ResultsType';
import { VpIcon } from '@vtmn-play/react/headless';
import { useTranslations } from 'next-intl';
import { Fragment } from 'react';
import { getResizedPictureUrl } from '@/app/utils/entities/media';
import { ResizedImg } from '~/components/commons/ResizedImg/ResizedImg';
import { PicturesGallery, usePicturesGallery } from '~/components/Interaction/Form/Question/Types/Choice/PicturesGallery';
import { getPicturesList } from '~/components/Interaction/Form/Question/utils';
import { roundToNDecimal } from '~/utils/utils';
import { IconTop3 } from '../Icons/icon-top-3';

export const RankingPictureResult = ({
  result,
  answerChoices,
}: RankingPictureResultPropsType) => {
  const t = useTranslations();

  const resultId = result?.result_id;
  const resultData = result?.data?.slice(0, 3);
  const totalParticipations = result?.participations_count;

  const getChoiceAverage = (item: Data) => {
    const top1 = item.answers_repartition.find(c => c.position === 1);
    return top1
      ? roundToNDecimal((top1.count / totalParticipations) * 100, 2)
      : 0;
  };

  const picturesList = getPicturesList(answerChoices);
  const pictureListGallery = picturesList.map(p =>
    getResizedPictureUrl(p, '1600x0'),
  );
  const { isOpen, setIsOpen, photoIndex, setPhotoIndex, openGallery }
    = usePicturesGallery(pictureListGallery);

  return (
    <div className="ranking-picture-result">
      <PicturesGallery
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        photoIndex={photoIndex}
        setPhotoIndex={setPhotoIndex}
        picturesList={pictureListGallery}
        picturesListFallback={picturesList}
      />
      <div className="ranking-picture-result__container">
        {resultData?.map((item: Data, index: number) => {
          const { points } = item;
          const average = getChoiceAverage(item);
          const choice = answerChoices?.find(q => q['@id'] === item['@id']);
          const choiceTitle = choice?.title;
          const choiceMediaUrl = item.media_url;
          const position = index + 1;

          return (
            <Fragment key={`ranking-picture-${resultId}-${index}`}>
              <div className="ranking-picture-result__line" />
              <div className="ranking-picture-result__podium">
                <div
                  className={`ranking-picture-result__position ranking-picture-result__position--${position}`}
                >
                  <IconTop3 />
                  <span>{position}</span>
                </div>
                <div className="ranking-picture-result__image">
                  {choiceMediaUrl
                    ? (
                        <>
                          <ResizedImg
                            alt=""
                            fill
                            format="220x0"
                            sizes="220px"
                            src={choiceMediaUrl}
                            style={{
                              objectFit: 'cover',
                            }}
                          />
                          <button
                            type="button"
                            className="ranking-picture-result__zoom"
                            onClick={() => openGallery(
                              getResizedPictureUrl(choiceMediaUrl, '1600x0'),
                            )}
                            title={t('GLOBAL.PICTURE_ZOOM')}
                            tabIndex={-1}
                          >
                            <VpIcon name="eye" size={24} />
                          </button>
                        </>
                      )
                    : (
                        <span
                          className="ranking-picture-result__fallback"
                          data-testid="fallback"
                        >
                          <VpIcon name="image" size={32} />
                        </span>
                      )}
                </div>
              </div>
              <div className="ranking-picture-result__title">
                <p data-cy="rankingPictureChoiceTitle" className="vp-title-s">
                  {choiceTitle}
                </p>
              </div>
              <div className="ranking-picture-result__stats">
                <p className="vp-title-m">
                  {t('SURVEY.RESULTS.RANKING_POINT', { count: points })}
                </p>
                <p className="vp-body-s">
                  {t('SURVEY.RESULTS.RANKING_PERCENTAGE', { count: average })}
                </p>
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};

export interface RankingPictureResultPropsType {
  result: ResultsQuestionsValueType<Data[]>;
  answerChoices: ChoiceType[];
}

interface Data {
  '@id': string;
  choice: string;
  choice_id: number;
  answers_count: number;
  points: number;
  media_url: UrlType;
  answers_repartition: {
    position: number;
    count: number;
    points: number;
  }[];
}
